import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
}

export const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const { user, loading, userRoles } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
      return;
    }

    // Server-side role verification for admin routes
    const verifyAccess = async () => {
      if (!loading && user && requiredRole === 'super_admin') {
        try {
          // Verify admin access server-side via RPC
          const { data: roles } = await supabase.rpc('get_user_roles', {
            _user_id: user.id
          });

          const hasAdminAccess = roles?.includes('super_admin') || roles?.includes('school_admin');
          
          if (!hasAdminAccess) {
            console.warn('Unauthorized admin access attempt blocked');
            navigate('/');
          }
        } catch (error) {
          console.error('Role verification failed:', error);
          navigate('/');
        }
        return;
      }

      // Check role-based access for other protected routes
      if (!loading && user && requiredRole && !userRoles.includes(requiredRole as any)) {
        // Super admins can access everything
        if (userRoles.includes('super_admin')) {
          return;
        }
        // Redirect to appropriate dashboard if user doesn't have required role
        navigate('/');
      }
    };

    verifyAccess();
  }, [user, loading, navigate, requiredRole, userRoles]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return <>{children}</>;
};
